import { useState } from 'react'
import { Modal, Box, TextField, CircularProgress } from '@mui/material'
import { fetchData } from '../../../../../global/utils/fetch'
import PrimaryButton from '../../../../../global/components/buttons/primary-button/PrimaryButton'
import styles from './styles.module.css'

interface IBatteryDataModalProps {
  isOpen: boolean
  handleClosed: () => void
  vanObjectId: string
  vanName: string
  hasBatteryData: boolean
}

function formatDateYyyyMmDd(date: Date): string {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}


export function BatteryDataModal({ isOpen, handleClosed, vanObjectId, vanName, hasBatteryData }: IBatteryDataModalProps) {
  const [loading, setLoading] = useState(false)
  const [reportData, setReportData] = useState<string | undefined>(undefined)
  
  const initialToDate = new Date()
  const initialFromDate = new Date()
  initialFromDate.setDate(initialToDate.getDate() - 7) // Default to last 7 days

  const [dates, setDates] = useState({
    fromDate: formatDateYyyyMmDd(initialFromDate),
    toDate: formatDateYyyyMmDd(initialToDate),
  })

  function handleDateChange(e: React.ChangeEvent<HTMLInputElement>) {
    setDates(prevState => ({
      ...prevState,
      [e.target.name]: e.target.value,
    }))
    setReportData(undefined)
  }

  async function handleGenerateReport() {
    if (!vanObjectId) {
      return
    }

    setLoading(true)
    setReportData(undefined)

    try {
      const query = new URLSearchParams({
        fromDate: new Date(dates.fromDate).toISOString(),
        toDate: new Date(dates.toDate).toISOString(),
        vanObjectId: vanObjectId,
      })

      const response = await fetchData<{ csv: string }>({
        method: 'GET',
        url: `${process.env.REACT_APP_ORIGIN}/reports/batteryDataReport?${query.toString()}`,
      })

      if (response && response.csv) {
        setReportData(response.csv)
      } else {
        setReportData('')
      }
    } catch (err) {
      console.error('Error generating battery report:', err)
      setReportData('')
    } finally {
      setLoading(false)
    }
  }

  function handleDownload() {
    if (!reportData) return

    const csvData = new Blob([reportData], {
      type: 'text/csv;charset=utf-8;',
    })
    const downloadLink = window.URL.createObjectURL(csvData)

    const link = document.createElement('a')
    link.href = downloadLink
    link.download = `battery_report_${vanName}_${dates.fromDate}_${dates.toDate}.csv`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    
    window.URL.revokeObjectURL(downloadLink)
  }

  function handleCloseModal() {
    setReportData(undefined)
    handleClosed()
  }

  return (
      <Modal open={isOpen} onClose={handleCloseModal}>
        <Box className={styles.downloadReportsModal}>
        <div className={styles.reportTitle}>Battery Data</div>
        
        <div className={styles.reportsLinkContainer}>
          <div className={styles.inputsContainer}>
            <TextField
              type="date"
              label="From"
              name="fromDate"
              value={dates.fromDate}
              onChange={handleDateChange}
              InputLabelProps={{ shrink: true }}
              disabled={loading}
            />
            <TextField
              type="date"
              label="To"
              name="toDate"
              value={dates.toDate}
              onChange={handleDateChange}
              InputLabelProps={{ shrink: true }}
              inputProps={{ min: dates.fromDate }}
              disabled={loading}
            />
            <div className={styles.inputsLabel}>
              Reports below require<br />a timeframe
            </div>
          </div>

          <div className={styles.reportContainer}>
            <div>
              <button
                style={{
                  color: hasBatteryData ? '#d70000' : '#ccc',
                  cursor: hasBatteryData ? 'pointer' : 'not-allowed',
                }}
                className={`${styles.navLink} ${styles.batteryDataLink}`}
                onClick={hasBatteryData ? handleGenerateReport : undefined}
                disabled={loading || !hasBatteryData}
              >
                Battery Data
              </button>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
              {loading && <CircularProgress size={20} />}
              {reportData === '' && <div>No data available to download</div>}
              {reportData && reportData !== '' && (
                <PrimaryButton
                  buttonName="Download"
                  onClick={handleDownload}
                />
              )}
            </div>
          </div>
        </div>
        </Box>
      </Modal>
  )
}